const DEFAULT_THRESHOLD_CENTS = 4500;

const DEFAULT_STANDARD_SHIPPING_CENTS = 395;

/**
 * @returns {Promise<{ items?: unknown[], total_price?: number, item_count?: number } | null>}
 */
async function fetchCartJson() {
  try {
    const url = new URL('/cart.js', window.location.origin);
    url.searchParams.set('_', String(Date.now()));

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

/**
 * @param {number} cents
 * @param {string} moneyFormat
 * @returns {string}
 */
function formatMoney(cents, moneyFormat) {
  const value = typeof cents === 'number' ? cents : 0;
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(value, moneyFormat || '${{amount}}');
  }
  return `$${(value / 100).toFixed(2)}`;
}

/**
 * @param {string} template
 * @param {string} amount
 * @returns {string}
 */
function applyAmountTemplate(template, amount) {
  return template.replaceAll('__AMOUNT__', amount).replaceAll('{{ amount }}', amount);
}

/**
 * @param {string} text
 * @returns {string}
 */
function decodeHtmlEntities(text) {
  if (!text) {
    return '';
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * @param {Event} event
 * @returns {{ items?: unknown[], total_price?: number, item_count?: number } | null}
 */
function getCartFromEvent(event) {
  if (!(event instanceof CustomEvent) || !event.detail) {
    return null;
  }

  const { detail } = event;

  if (detail.resource?.items) {
    return detail.resource;
  }

  if (detail.cart?.items) {
    return detail.cart;
  }

  if (detail.items) {
    return detail;
  }

  return null;
}

/**
 * @param {HTMLElement} root
 * @returns {{ labels: { away: string, unlocked: string }, remaining: number, subtotal: number, progress: number, itemCount: number } | null}
 */
function readMarkupState(root) {
  const configEl = root.querySelector('[data-shinzo-free-ship-state]');
  if (!configEl?.textContent) {
    return null;
  }

  try {
    const parsed = JSON.parse(configEl.textContent.trim());
    const labels = parsed?.labels;

    if (!labels?.away || !labels?.unlocked) {
      return null;
    }

    return {
      labels: {
        away: decodeHtmlEntities(String(labels.away)),
        unlocked: decodeHtmlEntities(String(labels.unlocked)),
      },
      remaining: Math.max(0, Number(parsed.remaining) || 0),
      subtotal: Math.max(0, Number(parsed.subtotal) || 0),
      progress: Math.min(100, Math.max(0, Number(parsed.progress) || 0)),
      itemCount: Math.max(0, Number(parsed.itemCount) || 0),
    };
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} root
 * @returns {number}
 */
function getThresholdCents(root) {
  return parseInt(root.getAttribute('data-threshold-cents') || '', 10) || DEFAULT_THRESHOLD_CENTS;
}

/**
 * @param {HTMLElement} root
 * @returns {string}
 */
function getMoneyFormat(root) {
  return root.getAttribute('data-money-format') || '${{amount}}';
}

/**
 * @param {HTMLElement} root
 * @param {{ labels: { away: string, unlocked: string }, remaining: number, progress: number, itemCount: number }} state
 */
function applyFreeShippingState(root, state) {
  const thresholdCents = getThresholdCents(root);

  if (state.itemCount < 1 || !(thresholdCents > 0)) {
    root.hidden = true;
    return;
  }

  root.hidden = false;

  const textEl = root.querySelector('[data-shinzo-free-ship-text]');
  if (textEl instanceof HTMLElement) {
    textEl.hidden = false;

    if (state.remaining > 0) {
      const amount = formatMoney(state.remaining, getMoneyFormat(root));
      textEl.textContent = applyAmountTemplate(state.labels.away, amount);
    } else {
      textEl.textContent = state.labels.unlocked;
    }
  }

  const barEl = root.querySelector('[data-shinzo-free-ship-bar]');
  const fillEl = root.querySelector('[data-shinzo-free-ship-fill]');

  if (fillEl instanceof HTMLElement) {
    fillEl.style.width = `${state.progress}%`;
    fillEl.style.backgroundColor = 'var(--shinzo-pdp-green, #8ba676)';
  }

  if (barEl instanceof HTMLElement) {
    barEl.setAttribute('aria-valuenow', String(state.progress));
    barEl.setAttribute('aria-valuemax', '100');
  }
}

/**
 * @param {HTMLElement} root
 * @param {{ total_price?: number, item_count?: number }} cart
 */
function applyCartToRoot(root, cart) {
  const markupState = readMarkupState(root);
  const labels = markupState?.labels ?? {
    away: 'You’re __AMOUNT__ away from free shipping.',
    unlocked: 'You’ve got free shipping!',
  };

  const thresholdCents = getThresholdCents(root);
  const count = cart?.item_count || 0;
  const subtotalCents = cart.total_price || 0;
  const remaining = isFreeShippingQualified(cart, thresholdCents)
    ? 0
    : Math.max(0, thresholdCents - subtotalCents);
  const progress =
    remaining <= 0
      ? 100
      : Math.min(100, Math.round((subtotalCents / thresholdCents) * 100));

  applyFreeShippingState(root, {
    labels,
    remaining,
    subtotal: subtotalCents,
    progress,
    itemCount: count,
  });
}

/**
 * @param {{ total_price?: number } | null | undefined} cart
 * @param {number} thresholdCents
 * @returns {boolean}
 */
function isFreeShippingQualified(cart, thresholdCents) {
  return (cart?.total_price || 0) >= thresholdCents;
}

/**
 * @param {{ total_price?: number, item_count?: number, items?: unknown[] } | null | undefined} [cart]
 */
function syncCartShippingLine(cart) {
  if (!cart?.items) {
    return;
  }

  const row = document.querySelector('[data-shinzo-cart-shipping]');
  const valueEl = row?.querySelector('[data-shinzo-cart-shipping-value]');

  if (!(row instanceof HTMLElement) || !(valueEl instanceof HTMLElement)) {
    return;
  }

  const thresholdCents =
    parseInt(row.getAttribute('data-threshold-cents') || '', 10) || DEFAULT_THRESHOLD_CENTS;
  const standardShippingCents =
    parseInt(row.getAttribute('data-standard-shipping-cents') || '', 10) ||
    DEFAULT_STANDARD_SHIPPING_CENTS;
  const freeLabel = row.getAttribute('data-shipping-free-label') || 'FREE';
  const moneyFormat = row.getAttribute('data-money-format') || '${{amount}}';
  const count = cart.item_count || 0;

  if (count < 1) {
    row.hidden = true;
    return;
  }

  row.hidden = false;

  const qualified = isFreeShippingQualified(cart, thresholdCents);
  valueEl.textContent = qualified ? freeLabel : formatMoney(standardShippingCents, moneyFormat);
  valueEl.dataset.freeShipping = qualified ? 'true' : 'false';
}

/**
 * @param {HTMLElement} root
 */
function applyMarkupToRoot(root) {
  const markupState = readMarkupState(root);
  if (!markupState) {
    return;
  }

  applyFreeShippingState(root, markupState);
}

/**
 * @param {{ total_price?: number, item_count?: number } | null} [cart]
 */
function syncAllFreeShipping(cart) {
  const roots = document.querySelectorAll('shinzo-cart-drawer-free-shipping');

  for (const root of roots) {
    if (!(root instanceof HTMLElement)) {
      continue;
    }

    if (cart?.items) {
      applyCartToRoot(root, cart);
      continue;
    }

    applyMarkupToRoot(root);
  }

  syncCartShippingLine(cart);
}

async function fetchCartAndSync() {
  const cart = await fetchCartJson();
  if (cart?.items) {
    syncAllFreeShipping(cart);
    return;
  }

  syncAllFreeShipping(null);
}

/** Run after Horizon morphSection updates the cart drawer DOM. */
function scheduleSyncAfterMorph(cart) {
  requestAnimationFrame(() => {
    if (cart?.items) {
      syncAllFreeShipping(cart);
    } else {
      syncAllFreeShipping(null);
    }

    requestAnimationFrame(() => {
      syncAllFreeShipping(cart ?? null);
    });
  });
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  const cart = getCartFromEvent(event);
  scheduleSyncAfterMorph(cart);

  if (!cart?.items) {
    fetchCartAndSync();
  }
}

/**
 * @param {HTMLElement} root
 */
function ensureTextFromMarkup(root) {
  const textEl = root.querySelector('[data-shinzo-free-ship-text]');
  if (!(textEl instanceof HTMLElement) || textEl.textContent?.trim()) {
    return;
  }

  applyMarkupToRoot(root);
}

class ShinzoCartDrawerFreeShipping extends HTMLElement {
  connectedCallback() {
    ensureTextFromMarkup(this);
    fetchCartAndSync();
  }
}

if (!customElements.get('shinzo-cart-drawer-free-shipping')) {
  customElements.define('shinzo-cart-drawer-free-shipping', ShinzoCartDrawerFreeShipping);
}

document.addEventListener('cart:update', handleCartUpdate);

function bindCartDrawerOpenSync() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!(drawer instanceof HTMLElement) || drawer.dataset.shinzoFreeShipOpenBound === 'true') {
    return;
  }

  drawer.dataset.shinzoFreeShipOpenBound = 'true';
  drawer.addEventListener('dialog:open', () => {
    scheduleSyncAfterMorph(null);
    fetchCartAndSync();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindCartDrawerOpenSync);
} else {
  bindCartDrawerOpenSync();
}
